import { useEffect, useRef } from "react"
import { getGameNotifications } from "@/api/gameNotificationsApi"
import { GAME_NOTIFY_FROM } from "@/constants/gameNotifications"
import {
  openResolvedGameNotificationLink,
  resolveGameNotificationLink,
} from "@/utils/gameNotificationLink"

const POLL_MS = 45_000
const SEEN_KEY = "gdaksby_web_notify_seen"

function loadSeenIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return new Set()
    return new Set(arr.filter((x) => typeof x === "number"))
  } catch {
    return new Set()
  }
}

function saveSeenIds(ids: Set<number>) {
  const trimmed = [...ids].slice(-200)
  sessionStorage.setItem(SEEN_KEY, JSON.stringify(trimmed))
}

export async function requestBrowserNotificationPermission(): Promise<boolean> {
  if (typeof Notification === "undefined") return false
  if (Notification.permission === "granted") return true
  if (Notification.permission === "denied") return false
  const result = await Notification.requestPermission()
  return result === "granted"
}

function showBrowserNotification(title: string, body: string, link: string | null) {
  if (typeof Notification === "undefined") return
  if (Notification.permission !== "granted") return
  const n = new Notification(title, {
    body,
    tag: `gdaksby-${title}-${body}`.slice(0, 120),
  })
  n.onclick = () => {
    window.focus()
    const resolved = resolveGameNotificationLink(link)
    openResolvedGameNotificationLink(resolved)
    n.close()
  }
}

/**
 * Пока бэкенд шлёт только Expo Push — на вебе опрос inbox + Browser Notification API.
 */
export function useGameWebNotifications(enabled: boolean) {
  const seenRef = useRef(loadSeenIds())
  const bootstrappedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const tick = async () => {
      if (document.visibilityState === "hidden") return
      try {
        const { items } = await getGameNotifications()
        const gameItems = items.filter(
          (n) => n.from?.trim() === GAME_NOTIFY_FROM && !n.isReaded
        )

        if (!bootstrappedRef.current) {
          gameItems.forEach((n) => seenRef.current.add(n.notificationId))
          bootstrappedRef.current = true
          saveSeenIds(seenRef.current)
          return
        }

        for (const item of gameItems) {
          if (seenRef.current.has(item.notificationId)) continue
          seenRef.current.add(item.notificationId)
          showBrowserNotification(item.title, item.body, item.link)
        }
        saveSeenIds(seenRef.current)
      } catch {
        // тихо — повторим на следующем интервале
      }
    }

    void tick()
    timer = setInterval(() => {
      if (!cancelled) void tick()
    }, POLL_MS)

    const onVisible = () => {
      if (document.visibilityState === "visible") void tick()
    }
    document.addEventListener("visibilitychange", onVisible)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [enabled])
}
